import styled, { css } from 'styled-components';
import { memo } from 'react';
import { Hideable } from '@regular_ui/lib/components/hideable';
import { layout, remCalc, colors } from '@prisjakt/ui-base';
import CloseIcon from 'mdi-react/CloseIcon';
import Clickable from '@prisjakt/ui-clickable';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import { Speed, Delay, Direction } from '@regular_ui/lib/definitions';
import { slideHideableVariant } from '@prisjakt/customization_prisjakt/variants/hideable';
import OverlayBase from '../../../../components/overlay';

const backgroundPeekWidth = 50;
const headerFontSize = '1rem';
const settingsButtonWidth = '34px';

export const Overlay = memo(styled(OverlayBase)`
  && {
    /* Cover the header so e.g. the search bar on the frontpage won't peek through */
    z-index: 101;
  }
`);

export const FakeHeader = memo(styled(Hideable).attrs({
  speed: Speed.Slow,
})`
  background: ${props => props.theme.header.bg};
  height: ${layout.headerHeight.mobile.rem};
  position: fixed;
  right: 0;
  top: 0;
  width: 100vw;
  /* Needs a higher z-index than the overlay */
  z-index: 102;
`);

export const Sidebar = styled(Hideable).attrs({
  variant: slideHideableVariant,
  speed: Speed.Slow,
  styledComponentProps: {
    slideDirection: Direction.Left,
    'data-test': 'SidebarMobile',
  },
})`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  /* Override default slide transform to make the menu button line up with the one in the header */
  ${props =>
    !props.visible &&
    css`
      && {
        transform: translateX(calc(${remCalc(backgroundPeekWidth)} - 100%));
      }
    `}
  /* Needs a higher z-index than the overlay */
  z-index: 102;
`;

type SwipeTargetProps = {
  height?: string;
};
export const SwipeTarget = styled.div<SwipeTargetProps>`
  width: 100vw;
  height: ${p => p.height};
`;

export const MenuButtonArea = styled(Hideable).attrs({
  speed: Speed.Slow,
})`
  height: ${layout.headerHeight.mobile.rem};
  background: ${props => props.theme.header.bg};
  position: absolute;
  right: 0;
  top: 0;
  /* Needs a higher z-index than the overlay */
  z-index: 102;
`;

export const CloseButton = styled.button.attrs({ type: 'button' })`
  align-items: center;
  color: ${props => props.theme.header.fg};
  cursor: pointer;
  display: flex;
  height: ${layout.headerHeight.mobile.rem};
  justify-content: center;
  width: ${remCalc(backgroundPeekWidth)};
  /* Needs a higher z-index than the overlay */
  z-index: 102;
`;

export const SidebarView = styled(Hideable).attrs({
  speed: Speed.Fast,
  delayIn: Delay.Short,
})`
  flex-direction: column;
  position: absolute;
  width: calc(100vw - ${remCalc(backgroundPeekWidth)});
  padding-left: ${layout.spacing.xSmall};
  padding-right: ${layout.spacing.xSmall};

  ${p =>
    !p.visible &&
    css`
      max-height: 100vh;
      overflow: hidden;
    `};
`;

export const SidebarHeader = styled(Clickable)`
  position: sticky;
  top: 0px;
  z-index: 1;
  background: ${colors.white};
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: ${headerFontSize};
  min-height: 50px;
  padding: ${layout.spacing.xxSmall} ${layout.spacing.xSmall};
  margin-bottom: ${layout.spacing.xSmall};
  padding-top: ${layout.spacing.xSmall};

  /* Set the width and box-sizing to account for inconsistencies between <button> and <a> */
  box-sizing: border-box;
  width: 100%;

  &:hover,
  &.focus-visible {
    background-color: ${props => props.theme.bg.secondary};
  }
`;

type HeaderTitleProps = {
  hasMargin: boolean;
};

export const HeaderTitle = styled.div<HeaderTitleProps>`
  flex: 1 1 auto;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-weight: 900;

  ${p =>
    p.hasMargin &&
    css`
      margin-right: ${settingsButtonWidth};
    `}
`;

export const Close = styled(CloseIcon).attrs({ size: 28 })``;

export const ViewContentWrapper = styled.div`
  padding: ${layout.spacing.xSmall};
`;

export const LeftArrow = styled(ChevronLeftIcon).attrs({ size: 20 })`
  flex: 0 0 auto;
  margin-right: ${layout.spacing.xSmall};
  path {
    fill: currentColor;
  }
`;

export const SettingsButton = styled.div`
  /* Reserve space for a button to center align text */
  width: ${settingsButtonWidth};
  height: ${settingsButtonWidth};
`;

export const SettingsIcon = styled(({ component }) => component)`
  path {
    fill: ${p => p.theme.icons.tertiary} !important;
  }
`;

export const OptionButton = styled.button`
  width: 100%;
  text-align: left;
  white-space: nowrap;
  padding: ${layout.spacing.xSmall};
  cursor: pointer;
  transition: ${layout.animations.buttonTransition};

  &:hover {
    background: ${p => p.theme.dropdowns.secondary.hoverBackground};
  }
`;
